import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { hydrologyOf } from '../hydrology'
import { useViewCtx } from '../context/ViewContext'
import { useT } from '../i18n'
import { OverlayToggles } from '../maps/OverlayToggles'
import { overlaysForRegion } from '../maps/overlayRegion'
import { useFrameRegion } from '../maps/useFrameRegion'
import { FleetToggle } from '../models/FleetToggle'
import { segmentForFlag } from '../models/fleetSegments'
import type { VesselSearchHit } from '../api'
import { FlagPicker } from '../models/FlagPicker'
import { VesselSearch } from '../models/VesselSearch'
import { VesselGlyph } from '../models/VesselGlyph'
import { vesselColor } from '../map/vesselMarkerStyle'
import { speciesForVessel } from '../map/vesselFocus'
import { HintDot } from '../help/HintDot'
import { IsothermControl } from '../maps/IsothermControl'
import { FrontsControl } from '../maps/FrontsControl'
import { OverlaySection } from '../maps/OverlaySection'
import { WAVE_SWATCH } from '../maps/overlaySwatch'
import {
  effectiveIsobathStep,
  ISOBATH_DEEPEST_M,
  ISOBATH_DEEP_STEP_M,
  ISOBATH_SHELF_M,
  ISOBATH_STEP_DEFAULT,
  ISOBATH_STEP_MAX,
  ISOBATH_STEP_MIN,
  ISOBATH_STEP_SLIDER,
  ISOBATH_SWATCH,
  isobathStepLabel,
} from '../map/isobathStep'
import { ISO_MIN, ISO_MAX } from '../maps/isothermMath'
import { ISO_SPREAD_DEFAULT } from '../map/isothermFamily'
import {
  convertStrength,
  defaultStrength,
  frontStrengthBounds,
  type FrontUnit,
} from '../maps/frontsMath'
import {
  WAVE_STEP_MIN,
  WAVE_STEP_MAX,
  WAVE_STEP_SLIDER_STEP,
  waveLabelText,
} from '../map/waveContours'

// Стартовая изотерма при включении — тёплая рабочая полоса, в фикс. диапазоне 5–16.
const DEFAULT_ISO = 12
/** Стартовая ПОВЕРХНОСТНАЯ изотерма (владелец 2026-08-19). Та же тёплая полоса:
 *  на 0 м вода всегда не холоднее, чем на горизонте Контекста. */
const DEFAULT_ISO_SURFACE = 12

/**
 * Корневой блок «Наложения»: оверлеи (туман/облачность/землетрясения/лёд/луна) +
 * флот (свой/РФ/ин.флот). Общий над разделами «Карты» и «Аналитика» — рисуются на
 * карте обоих (см. useOverlays / useFleetLayer), управляются из одного места.
 */
export function OverlaysBlock() {
  // Зум — из URL: вид карты контекст держит в ref (перерисовки он не вызывает),
  // а lng/lat/zoom пишет туда сама страница на moveend — той же руки, что
  // перезапрашивает изобаты. Значит подпись про шаг и линии на карте меняются
  // одним движением, а не спорят между собой.
  const [mapParams] = useSearchParams()
  // Ключа нет (карта ещё не двигалась) — это «зум неизвестен», а НЕ ноль:
  // Number(null) даёт 0, то есть самый обзорный зум, и панель объявляла бы
  // огрубление до 200 м на любом свежем входе.
  const zoomRaw = mapParams.get('zoom')
  const mapZoom = zoomRaw ? Number(zoomRaw) : NaN
  const {
    overlays,
    toggleOverlay,
    fleet,
    toggleFleet,
    date,
    species,
    setSpecies,
    showUncategorized,
    setShowUncategorized,
    flags,
    toggleFlagFilter,
    flagCounts,
    setFocusVessel,
    isobathStep,
    setIsobathStep,
    waveOverlay,
    setWaveOverlay,
    waveStep,
    setWaveStep,
    isotherm,
    setIsotherm,
    isothermSurface,
    setIsothermSurface,
    isothermSpread,
    setIsothermSpread,
    fronts,
    setFronts,
    frontsUnit,
    setFrontsUnit,
  } = useViewCtx()

  // Шаг, которым изобаты НАРИСОВАНЫ: на обзорных зумах сервер огрубляет
  // запрошенный (лестница ISOBATH_STEP_BY_ZOOM), и панель показывает именно
  // его. Выбор человека при этом цел — вернётся, как только карту приблизят.
  const isobathChosen = isobathStep ?? ISOBATH_STEP_DEFAULT
  const isobathDrawnStep = effectiveIsobathStep(
    isobathChosen,
    Number.isFinite(mapZoom) ? mapZoom : null,
  )
  const isobathClamped = isobathDrawnStep !== isobathChosen
  const { data } = useQuery({ queryKey: ['cabinet', 'layers'], queryFn: () => cabinetApi.layers() })
  const catalog = useMemo(() => data?.layers ?? [], [data])
  // Оверлей, не уместный в районе кадра (лёд над Аравийским морем), тумблера не
  // получает: он рисовал бы пустоту.
  const frameRegion = useFrameRegion()
  const regionCatalog = useMemo(() => overlaysForRegion(catalog, frameRegion), [catalog, frameRegion])
  // Признак «Показ гидрологии»: изотермы, термофронты и волнение своего слоя не
  // имеют, и в каталоге их нет — прятать приходится по признаку.
  const hydrology = hydrologyOf(data)
  // Виды, доступные ЭТОЙ организации: подставлять вид, которого нет в списке,
  // нельзя — вернуться назад тем же путём человек не смог бы.
  const { data: speciesData } = useQuery({
    queryKey: ['cabinet', 'species'],
    queryFn: () => cabinetApi.species(),
    staleTime: Infinity,
  })
  const availableSpecies = useMemo(
    () => (speciesData?.species ?? []).map(s => s.species_id),
    [speciesData],
  )
  const frontBounds = frontStrengthBounds(frontsUnit)
  const t = useT()

  // Ссылка извне несёт состояние инструментов (`iso=7.5`, `front=…`, `waves=1`),
  // и спрятанный переключатель его не отменяет: карта продолжала бы просить
  // сетку температуры и получать отказ. Гасим сам инструмент, а не только чип.
  useEffect(() => {
    if (hydrology) return
    if (isotherm != null) setIsotherm(null)
    // Дочерние линии изотермы — та же среда: оставшись, они просили бы сетку
    // SST и получали 403 при спрятанном тумблере.
    if (isothermSurface != null) setIsothermSurface(null)
    if (isothermSpread != null) setIsothermSpread(null)
    if (fronts != null) setFronts(null)
    if (waveOverlay) setWaveOverlay(false)
  }, [
    hydrology, isotherm, isothermSurface, isothermSpread, fronts, waveOverlay,
    setIsotherm, setIsothermSurface, setIsothermSpread, setFronts, setWaveOverlay,
  ])

  // Дочерние живут только при включённой основной изотерме: их тумблеры стоят
  // ВНУТРИ её блока и вместе с ним исчезают. Рукотворная ссылка `?isosurf=12`
  // без `iso` иначе дала бы линию, которую нечем выключить.
  useEffect(() => {
    if (isotherm != null) return
    if (isothermSurface != null) setIsothermSurface(null)
    if (isothermSpread != null) setIsothermSpread(null)
  }, [isotherm, isothermSurface, isothermSpread, setIsothermSurface, setIsothermSpread])

  // Выбор судна в поиске: карта летит к нему (ViewContext → useVesselFocus).
  // Заодно включаем нужный сегмент флота, если он выключен: иначе перелёт
  // приводит в пустое море — судна на карте просто нет.
  //
  // И подставляем вид промысла, если категория судна на него однозначно
  // указывает (владелец 2026-08-09): прилететь к сайровику и остаться на поле
  // сельди — значит смотреть не на ту воду.
  const pickVessel = (v: VesselSearchHit) => {
    const seg = segmentForFlag(v.flag_state)
    if (!fleet.includes(seg)) toggleFleet(seg)
    const next = speciesForVessel(v, availableSpecies)
    if (next != null && next !== species) setSpecies(next)
    setFocusVessel(v)
  }

  // Смена единицы сохраняет физический порог (переводим значение).
  const changeFrontsUnit = (unit: FrontUnit) => {
    if (fronts != null) setFronts(convertStrength(fronts, frontsUnit, unit))
    setFrontsUnit(unit)
  }

  return (
    <div className="space-y-4 border-b border-hull-hair px-3 pb-5 pt-4">
      <div className="eyebrow flex items-center gap-1.5">
        {t('overlays.title')}
        <HintDot id="overlays" />
      </div>

      <OverlayToggles layers={regionCatalog} active={overlays} onToggle={toggleOverlay} />
      <div className="space-y-1.5">
        <div className="eyebrow mb-2 flex items-center gap-1.5">
          {t('overlays.fleet')}
          <HintDot id="fleet" />
        </div>
        {/* Поиск — НАД кнопками сегментов: судно ищут независимо от того, какой
            слой включён, и найденное показываем даже при выключенном флоте. */}
        <VesselSearch onPick={pickVessel} />
        <FleetToggle segments={fleet} onToggle={toggleFleet} />
        {fleet.length > 0 && (
          <>
            {/* Галка «суда без категорий» осмысленна только при выбранном виде:
                без вида все суда и так показаны. */}
            {species != null && (
              <button
                type="button"
                role="switch"
                aria-checked={showUncategorized}
                aria-label={t('overlays.showUncategorized')}
                onClick={() => setShowUncategorized(!showUncategorized)}
                className="flex w-full items-center gap-2 text-left text-[11px] text-mist"
              >
                <span
                  className={
                    'relative h-4 w-7 shrink-0 rounded-full transition-colors ' +
                    (showUncategorized ? 'bg-sonar' : 'bg-hull')
                  }
                >
                  <span
                    className="absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all"
                    style={{ left: showUncategorized ? 14 : 2 }}
                  />
                </span>
                {t('overlays.showUncategorized')}
              </button>
            )}
            {/* Выбор флагов — только у «Ин.флота»: свой флот и РФ им не
                управляются, а список строится по судам текущего кадра. */}
            {fleet.includes('foreign') && (
              <FlagPicker counts={flagCounts} selected={flags} onToggle={toggleFlagFilter} />
            )}

            {/* Легенда возраста позиции. Значок — ТОТ ЖЕ силуэт, что на карте:
                кружки обещали фигуру, которой там давно нет. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-faint">
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <VesselGlyph color={vesselColor().fresh} />
                {t('overlays.vesselFresh')}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <VesselGlyph color={vesselColor().stale} />
                {t('overlays.vesselStale')}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <VesselGlyph color={vesselColor().stale} hollow />
                {t('overlays.vesselNoCourse')}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Единый инструмент температуры (изотерма + градиенты), фикс 0–16 °C, над
          любым базовым слоем — рисуется на карте обоих разделов (useTemperatureTools). */}
      {hydrology && (
        <>
          <IsothermControl
            enabled={isotherm != null}
            value={isotherm ?? DEFAULT_ISO}
            min={ISO_MIN}
            max={ISO_MAX}
            onToggle={on => setIsotherm(on ? DEFAULT_ISO : null)}
            onChange={setIsotherm}
            surface={isothermSurface}
            surfaceDefault={DEFAULT_ISO_SURFACE}
            onSurfaceToggle={on => setIsothermSurface(on ? DEFAULT_ISO_SURFACE : null)}
            onSurfaceChange={setIsothermSurface}
            spread={isothermSpread}
            spreadDefault={ISO_SPREAD_DEFAULT}
            onSpreadToggle={on => setIsothermSpread(on ? ISO_SPREAD_DEFAULT : null)}
            onSpreadChange={setIsothermSpread}
            hintsDate={date}
            species={species}
          />
          <FrontsControl
            enabled={fronts != null}
            value={fronts ?? defaultStrength(frontsUnit)}
            unit={frontsUnit}
            min={frontBounds.min}
            max={frontBounds.max}
            onToggle={on => setFronts(on ? defaultStrength(frontsUnit) : null)}
            onChange={setFronts}
            onUnitChange={changeFrontsUnit}
          />
        </>
      )}

      {/* Изобаты: линии равных глубин с выбранным шагом. Линии не считаются на
          запрос — берутся готовыми из таблицы (0…−1000 м через 10 м, глубже —
          до −6000 м через 500 м), поэтому ниже шельфа шаг свой, а глубже 6000 м
          линий нет, и раздел говорит об этом вслух. */}
      <OverlaySection
        title={t('overlays.isobaths.title')}
        hint="isobaths"
        swatch={ISOBATH_SWATCH}
        enabled={isobathStep != null}
        toggleLabel={t('overlays.isobaths.toggle')}
        onToggle={on => setIsobathStep(on ? ISOBATH_STEP_DEFAULT : null)}
      >
        <div className="mb-0.5 flex items-baseline gap-1">
          {/* Крупное число — шаг, которым НАРИСОВАНО. Показывать выбранные
              10 м, пока на карте лежат линии через 200, значило бы врать
              подписью (та же беда, что была у потолка линий). */}
          <span className="tnum text-2xl font-bold leading-none text-foam">
            {isobathStepLabel(isobathDrawnStep)}
          </span>
          <span className="text-xs font-medium text-sonar">{t('units.m')}</span>
        </div>
        <div className="mb-2 text-[11px] text-mist">{t('overlays.isobaths.step')}</div>
        <input
          type="range"
          min={ISOBATH_STEP_MIN}
          max={ISOBATH_STEP_MAX}
          step={ISOBATH_STEP_SLIDER}
          value={isobathStep ?? ISOBATH_STEP_DEFAULT}
          onChange={e => setIsobathStep(Number(e.target.value))}
          aria-label={t('overlays.isobaths.step')}
          className="isotherm-range w-full"
        />
        <div className="mt-1 flex justify-between tnum text-[10px] text-mist">
          <span>{t('overlays.denser')}</span>
          <span>{t('overlays.sparser')}</span>
        </div>
        {isobathClamped && (
          <div className="mt-1.5 text-[10px] text-buoy">
            {t('overlays.isobaths.zoomClamp', {
              step: isobathDrawnStep,
              wanted: isobathStep ?? ISOBATH_STEP_DEFAULT,
            })}
          </div>
        )}
        <div className="mt-1.5 text-[10px] text-faint">
          {t('overlays.isobaths.deepest', {
            shelf: ISOBATH_SHELF_M,
            deepStep: ISOBATH_DEEP_STEP_M,
            depth: ISOBATH_DEEPEST_M,
          })}
        </div>
      </OverlaySection>

      {/* Волнение: высота волны (оценка по ветру) красит стрелки течений
          #0f172a→красный. Стрелки те же и той же длины — меняется только цвет.
          Шаг изолиний пересчитывается браузером по кэшированной сетке (без сети). */}
      {hydrology && (
        <OverlaySection
          title={t('overlays.waves.title')}
          hint="waves"
          swatch={WAVE_SWATCH}
          enabled={waveOverlay}
          toggleLabel={t('overlays.waves.toggle')}
          onToggle={setWaveOverlay}
        >
          <div className="mb-0.5 flex items-baseline gap-1">
            <span className="tnum text-2xl font-bold leading-none text-foam">{waveLabelText(waveStep)}</span>
            <span className="text-xs font-medium text-sonar">{t('units.m')}</span>
          </div>
          <div className="mb-2 text-[11px] text-mist">{t('overlays.waves.step')}</div>
          <input
            type="range"
            min={WAVE_STEP_MIN}
            max={WAVE_STEP_MAX}
            step={WAVE_STEP_SLIDER_STEP}
            value={waveStep}
            onChange={e => setWaveStep(Number(e.target.value))}
            aria-label={t('overlays.waves.step')}
            className="isotherm-range w-full"
          />
          <div className="mt-1 flex justify-between tnum text-[10px] text-mist">
            <span>{t('overlays.denser')}</span>
            <span>{t('overlays.sparser')}</span>
          </div>
        </OverlaySection>
      )}
    </div>
  )
}
